import { useState } from "react";

import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { LoginForm, LoginFormValues } from "@/components/core/auth/login-form";
import { useAuth } from "@/core/auth/auth";

const fallback = "/";

export const Route = createFileRoute("/login")({
  component: Login,
  validateSearch: z.object({
    redirect: z.string().optional().catch(""),
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: search.redirect ?? fallback });
    }
  },
});

function Login() {
  const auth = useAuth();
  const router = useRouter();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (data: LoginFormValues): Promise<boolean> => {
    setLoading(true);
    setError("");
    try {
      await auth.login(data.email, data.password);
      await router.invalidate();
      // Short delay for better UX before redirecting
      await new Promise((resolve) => setTimeout(resolve, 500));
      return true;
    } catch (error) {
      // Try to extract a user-friendly error message
      let message = t("login-form.error.unexpected");
      // Try to detect TanStack Query/axios/fetch error shapes
      if (
        typeof error === "object" &&
        error !== null &&
        "status" in error &&
        typeof (error as { status?: unknown }).status === "number" &&
        ((error as { status: number }).status === 403 || (error as { status: number }).status === 401)
      ) {
        message = t("login-form.error.invalid_credentials");
      } else if (error instanceof Error) {
        if (error.message.includes("Network Error")) {
          message = t("login-form.error.network");
        } else if (/4\d\d|5\d\d/.test(error.message)) {
          message = t("login-form.error.auth_failed");
        } else {
          message = error.message;
        }
      }
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return <LoginForm onSubmit={handleLogin} loading={loading} error={error} />;
}
